import React, { useMemo, useState } from 'react'
import { Text } from '../ui'
import type {
  SupportCenterAttendanceDemandSelection,
  SupportCenterAttendanceDemandType,
} from '../../types/supportCenterPsychAttendance'

const inputClass =
  'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60'

function normalizeSearch(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
}

type AttendanceDemandSelectorProps = {
  demandTypes: SupportCenterAttendanceDemandType[]
  selection: SupportCenterAttendanceDemandSelection
  disabled?: boolean
  readOnly?: boolean
  searchable?: boolean
  onChange: (next: SupportCenterAttendanceDemandSelection) => void
}

export function getSelectedDemandTypesRequiringNotes(
  demandTypes: SupportCenterAttendanceDemandType[],
  selection: SupportCenterAttendanceDemandSelection
): SupportCenterAttendanceDemandType[] {
  const selectionById = new Map(selection.map((item) => [item.demand_type_id, item]))
  return demandTypes.filter((item) => selectionById.has(item.id) && item.requires_notes)
}

export const AttendanceDemandSelector: React.FC<AttendanceDemandSelectorProps> = ({
  demandTypes,
  selection,
  disabled = false,
  readOnly = false,
  searchable = false,
  onChange,
}) => {
  const [query, setQuery] = useState('')
  const demandTypesWithNotes = getSelectedDemandTypesRequiringNotes(demandTypes, selection)
  const selectedIds = new Set(selection.map((item) => item.demand_type_id))
  const selectionById = new Map(selection.map((item) => [item.demand_type_id, item]))
  const normalizedQuery = normalizeSearch(query.trim())
  const visibleDemandTypes = useMemo(() => {
    if (!searchable || !normalizedQuery) return demandTypes
    return demandTypes.filter((item) => normalizeSearch(item.name).includes(normalizedQuery))
  }, [demandTypes, normalizedQuery, searchable])

  const toggle = (id: string) => {
    const exists = selectedIds.has(id)
    if (exists) {
      onChange(selection.filter((item) => item.demand_type_id !== id))
      return
    }
    onChange([...selection, { demand_type_id: id, notes: '' }])
  }

  const updateNotes = (id: string, notes: string) => {
    onChange(
      selection.map((item) => (item.demand_type_id === id ? { ...item, notes } : item))
    )
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Tipo de demanda {!readOnly && <span className="text-red-500">*</span>}
        </label>
        {demandTypes.length === 0 ? (
          <Text size="sm" color="muted">
            Nenhuma demanda cadastrada para esta assistência.
          </Text>
        ) : (
          <>
            {searchable && (
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filtrar tipos de demanda..."
                aria-label="Filtrar tipos de demanda"
                className={`${inputClass} mb-2`}
              />
            )}
            {visibleDemandTypes.length === 0 ? (
              <Text size="sm" color="muted">
                Nenhum tipo encontrado para essa busca.
              </Text>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {visibleDemandTypes.map((item) => (
                  <label
                    key={item.id}
                    className="inline-flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item.id)}
                      disabled={disabled}
                      onChange={() => toggle(item.id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    {item.name}
                  </label>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {demandTypesWithNotes.map((item) => (
        <div key={item.id}>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Complemento para {item.name} {!readOnly && <span className="text-red-500">*</span>}
          </label>
          <textarea
            rows={3}
            value={selectionById.get(item.id)?.notes ?? ''}
            disabled={disabled}
            onChange={(e) => updateNotes(item.id, e.target.value)}
            className={`${inputClass} resize-none`}
            placeholder="Descreva a demanda quando necessário..."
          />
        </div>
      ))}
    </div>
  )
}
